import { defineStore } from 'pinia'
import { DateTime } from 'luxon'
import {
  ApiRequestResult,
  BooleanApiResult,
  ChangePasswordResult,
  CreateSubscriptionCheckoutSessionResult,
  DisableMfaSmsResult,
  EnableMfaSmsResult,
  type IChangePasswordParam,
  type ICreateCheckoutSessionRequest,
  type ICreateCustomerPortalSessionRequest,
  type ICreateSubscriptionCheckoutSessionRequest,
  type ICreateSubscriptionCheckoutSessionResult,
  type IDateFormat,
  type IProcessEmailChangeRequest,
  type ISubscription,
  CreateCustomerPortalSessionRequest
} from '@/models/models'
import constants from '../exports/constants'
import helpers from '../exports/helper'
import type { IAccountSettingsState } from '@/models/stores/accountSettings'
import httpClient from '@/httpClient'
import httpClientAnonymous from '@/httpClient'
import type {
  IDateFormats,
  IPlan,
  IRouterLinkToObject,
  ITimeZone,
  ITosResult,
  IUserSubscription
} from '@/models/interfaces'
import ErrorHelper from '../exports/error'
import { useCommonStore } from './CommonStore'
import { useLoginStore } from './LoginStore'
import { useModals } from '@/composables/useModal/useModal'
import i18n from '@/i18n'
import accountSettingsMessages from '@/pages/accounts/locales/accountSettings.en.json'
import accountSettingsMessagesEs from '@/pages/accounts/locales/accountSettings.es.json'
import { createDefaultAccountSubscriptionInfo } from '@/pages/accounts/config/accountSettings.types'
import { getAccountSettingsErrorKey } from '@/pages/accounts/config/accountSettings.errors'
import { CreateCustomerPortalSessionError } from '@/pages/accounts/config/accountSettings.enums'

interface AccountSettingsErrorResponse {
  errorCode?: number | null
  errorMessage?: string | null
}

function getLocalizedAccountSettingsError(
  context: string,
  response: AccountSettingsErrorResponse
): string {
  const key = getAccountSettingsErrorKey(context, response)

  return key ? i18n.global.t(key) : (response.errorMessage ?? '')
}

const initialState = (): IAccountSettingsState => ({
  phoneInfo: {},
  callingBalance: null,
  plans: [] as IPlan[],
  timeZones: [],
  dateFormats: [] as IDateFormats[],
  smsAccount: {},
  enableMfaSmsErrorMessage: '',
  verifyPhoneSmsErrorMessage: '',
  consentUrl: '',
  paywallPreviousPage: {},
  enableMfaSmsErrorCode: -1,
  verifyPhoneSmsErrorCode: -1,
  showAddMinutesSuccessToast: false
})

const paths: string[] = ['paywallPreviousPage']

export const useAccountSettingsStore = defineStore('accountSettings', {
  state: initialState,
  persist: { pick: paths },
  getters: {
    subscriptionInfo(): IUserSubscription {
      const commonStore = useCommonStore()
      return (
        commonStore.fullUserInfo.userSubscription ??
        createDefaultAccountSubscriptionInfo()
      )
    },
    subscriptionT0(): boolean {
      return (
        !!this.subscriptionInfo &&
        this.subscriptionInfo.highestTier == constants.subscriptionTiers.Free
      )
    },
    subscriptionT1(): boolean {
      return (
        !!this.subscriptionInfo &&
        this.subscriptionInfo?.highestTier ==
          constants.subscriptionTiers.Standard
      )
    },
    subscriptionT2(): boolean {
      return (
        !!this.subscriptionInfo &&
        this.subscriptionInfo?.highestTier ==
          constants.subscriptionTiers.Premium
      )
    },
    subscriptionT3(): boolean {
      return (
        !!this.subscriptionInfo &&
        this.subscriptionInfo?.highestTier == constants.subscriptionTiers.Basic
      )
    },
    smsNotificationsEnabled: (state) => {
      return state.smsAccount && (state.smsAccount?.balance || 0) > 0
    },
    getActiveSubscription(): ISubscription | undefined {
      const _activeSubcription =
        !helpers.isNullEmpty(this.subscriptionInfo) &&
        this.subscriptionInfo?.activeSubscriptions?.length == 1
          ? this.subscriptionInfo?.activeSubscriptions[0]
          : this.subscriptionInfo?.activeSubscriptions?.find(
              (subscription) =>
                subscription.tier == this.subscriptionInfo?.highestTier
            )

      return _activeSubcription
    },
    isActiveSubscriptionTrial(): boolean {
      return this.getActiveSubscription?.trial ?? false
    },
    getAccountStatus(): number {
      let _status = constants.subscriptionStatus.None
      if (this.getActiveSubscription) {
        _status = this.getActiveSubscription.canceled
          ? constants.subscriptionStatus.Canceled
          : constants.subscriptionStatus.Active
      }
      return _status
    },
    getAccountType(): number {
      let _type = constants.subscriptionTypes.None
      if (this.getActiveSubscription) {
        _type =
          this.getActiveSubscription.type || constants.subscriptionTypes.None
      }
      return _type
    },
    canAccountUpgradeOrCancel(): boolean {
      return (
        this.getAccountType == constants.subscriptionTypes.None ||
        this.getAccountType == constants.subscriptionTypes.Braintree ||
        this.getAccountType == constants.subscriptionTypes.Stripe
      )
    },
    getSubscriptionExpiresDate(): DateTime | null {
      const sub = this.getActiveSubscription
      if (sub) {
        return DateTime.fromISO(sub.expiresDate as unknown as string) // string has offset + converted in format fn
      }
      return null
    },
    getHasSubscriptionIssue(): number | undefined {
      return this.subscriptionInfo.duplicateSubscriptionError
    },
    getHasBillingIssue(): boolean {
      return (
        (this.getActiveSubscription &&
          this.getActiveSubscription.hasBillingIssue) ||
        false
      )
    },
    isAppleSubscription(): boolean {
      return (
        this.getAccountType == constants.subscriptionTypes.Apple ||
        this.getAccountType == constants.subscriptionTypes.AppleRenewing
      )
    },
    isAndroidSubscription(): boolean {
      return this.getAccountType == constants.subscriptionTypes.PlayStore
    },
    isPromotional(): boolean {
      return this.getAccountType == constants.subscriptionTypes.Promotional
    },
    isFeeWaiver(): boolean {
      return this.getActiveSubscription?.isFeeWaiver ?? false
    },
    getPlanName(): string {
      if (!i18n.global.te('accountSettings.planDetails.planName.free')) {
        i18n.global.mergeLocaleMessage('en', {
          accountSettings: accountSettingsMessages
        })
        i18n.global.mergeLocaleMessage('es', {
          accountSettings: accountSettingsMessagesEs
        })
      }

      if (this.subscriptionT0) {
        return i18n.global.t('accountSettings.planDetails.planName.free')
      } else if (this.subscriptionT1) {
        return i18n.global.t('accountSettings.planDetails.planName.enhanced')
      } else if (this.subscriptionT2) {
        return i18n.global.t('accountSettings.planDetails.planName.ultimate')
      } else {
        return i18n.global.t('accountSettings.planDetails.planName.basic')
      }
    },
    getTierName(): string {
      if (!i18n.global.te('accountSettings.planDetails.tiers.ultimate')) {
        i18n.global.mergeLocaleMessage('en', {
          accountSettings: accountSettingsMessages
        })
        i18n.global.mergeLocaleMessage('es', {
          accountSettings: accountSettingsMessagesEs
        })
      }

      if (this.subscriptionT0) {
        return i18n.global.t('accountSettings.planDetails.tiers.free')
      } else if (this.subscriptionT1) {
        return i18n.global.t('accountSettings.planDetails.tiers.enhanced')
      } else if (this.subscriptionT2) {
        return i18n.global.t('accountSettings.planDetails.tiers.ultimate')
      } else {
        return i18n.global.t('accountSettings.planDetails.tiers.basic')
      }
    },
    appStoreSvgUrl(): string {
      return 'https://media.talkingparents.com/images/app-store.svg'
    },
    playStoreSvgUrl(): string {
      return 'https://media.talkingparents.com/images/google-play.svg'
    },
    getTiers: () => [
      constants.subscriptionTiers.Premium,
      constants.subscriptionTiers.Standard,
      constants.subscriptionTiers.Basic
    ],
    getBillingPeriod(): number | null {
      return this.getActiveSubscription?.billingPeriod
        ? this.getActiveSubscription.billingPeriod
        : null
    },
    activeMonthlySubscription(): boolean {
      return this.getBillingPeriod
        ? this.getBillingPeriod === constants.billingPeriod.monthly
        : false
    },
    activeAnnualSubscription(): boolean {
      return this.getBillingPeriod
        ? this.getBillingPeriod === constants.billingPeriod.annual
        : false
    },
    getCurrentSubscriptionTier(): number {
      return this.subscriptionInfo?.highestTier ?? 0
    }
  },
  actions: {
    reset() {
      Object.assign(
        this.$state,
        helpers.omit(initialState(), paths as (keyof IAccountSettingsState)[])
      )
    },
    async createSubscriptionCheckoutSession(
      payload: ICreateSubscriptionCheckoutSessionRequest
    ): Promise<ICreateSubscriptionCheckoutSessionResult> {
      try {
        const response = await httpClient.post(
          '/web/api/payments/CreateSubscriptionCheckoutSession',
          payload
        )

        if (!response?.data?.success)
          throw new Error(response?.data?.errorMessage)

        return CreateSubscriptionCheckoutSessionResult.fromJS(response.data)
      } catch (e) {
        ErrorHelper.handleError(e, 'createSubscriptionCheckoutSession')
        return {}
      }
    },
    async createCustomerPortalSession(
      payload: ICreateCustomerPortalSessionRequest
    ): Promise<ICreateSubscriptionCheckoutSessionResult> {
      try {
        const response = await httpClient.post(
          '/web/api/payments/CreateCustomerPortalSession',
          payload
        )

        return CreateSubscriptionCheckoutSessionResult.fromJS(response.data)
      } catch (e) {
        ErrorHelper.handleError(e, 'createCustomerPortalSession')
        return {}
      }
    },
    async createCheckoutSession(
      payload: ICreateCheckoutSessionRequest
    ): Promise<ICreateSubscriptionCheckoutSessionResult> {
      try {
        const response = await httpClient.post(
          '/web/api/payments/CreateCheckoutSessionForSms',
          payload
        )

        if (!response?.data?.success)
          throw new Error(response?.data?.errorMessage)

        return CreateSubscriptionCheckoutSessionResult.fromJS(response.data)
      } catch (e) {
        ErrorHelper.handleError(e, 'createCustomerPortalSession')
        return {}
      }
    },
    async createCheckoutSessionForAddMinutes(
      payload: ICreateCheckoutSessionRequest
    ): Promise<ICreateSubscriptionCheckoutSessionResult> {
      try {
        const response = await httpClient.post(
          '/web/api/payments/CreateCheckoutSessionForAddMinutes',
          payload
        )

        if (!response?.data?.success)
          throw new Error(response?.data?.errorMessage)

        return CreateSubscriptionCheckoutSessionResult.fromJS(response.data)
      } catch (e) {
        ErrorHelper.handleError(e, 'createCustomerPortalSession')
        return {}
      }
    },
    async cancelProcessing(sessionId: string): Promise<void> {
      try {
        await httpClient.get(
          '/web/api/payments/CancelProcessing?session_id=' + sessionId
        )
      } catch (e) {
        ErrorHelper.handleError(e, 'cancelProcessing')
      }
    },
    async orderSuccess(sessionId: string): Promise<boolean> {
      try {
        const response = await httpClient.get(
          '/web/api/payments/OrderSuccess?session_id=' + sessionId
        )

        if (!response?.data?.success)
          throw new Error(response?.data?.errorMessage)

        return true
      } catch (e) {
        ErrorHelper.handleError(e, 'orderSuccess')
        return false
      }
    },
    async smsSuccess(sessionId: string): Promise<void> {
      try {
        const response = await httpClient.get(
          '/web/api/payments/smsSuccess?session_id=' + sessionId
        )
        if (!response?.data?.success)
          throw new Error(response?.data?.errorMessage)
      } catch (e) {
        ErrorHelper.handleError(e, 'smsSuccess')
      }
    },
    async addMinSuccess(sessionId: string) {
      try {
        const response = await httpClient.get(
          '/web/api/payments/addMinutesSuccess?session_id=' + sessionId
        )
        if (!response?.data?.success)
          throw new Error(response?.data?.errorMessage)
      } catch (e) {
        ErrorHelper.handleError(e, 'smsSuccess')
      }
    },
    async fetchPhoneInfo() {
      try {
        const response = await httpClient.get(
          '/web/api/MyAccount/GetUserMobilePhoneInfo'
        )
        if (!response?.data?.success)
          throw new Error(response?.data?.errorMessage)

        this.phoneInfo = response.data.value
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchPhoneInfo')
      }
    },
    async fetchCallingBalance() {
      try {
        const response = await httpClient.get(
          '/web/api/MyAccount/GetCompleteCallingBalance'
        )
        if (!response?.data?.success)
          throw new Error(response?.data?.errorMessage)

        this.callingBalance = response.data.value
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchCallingBalance')
      }
    },
    async fetchPlans() {
      try {
        const response = await httpClient.get('/web/api/BuyMinutes/GetPlans')
        if (!response?.data?.success)
          throw new Error(response?.data?.errorMessage)

        this.plans = response.data.value
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchPlans')
      }
    },
    async fetchTimeZones() {
      try {
        const response = await httpClientAnonymous.get(
          '/web/api/MyAccount/GetTimeZones'
        )
        if (!response?.data?.success)
          throw new Error(response?.data?.errorMessage)

        this.timeZones = response.data.value
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchTimeZones')
      }
    },
    async fetchDateFormats() {
      try {
        const response = await httpClient.get(
          '/web/api/MyAccount/GetDateFormats'
        )
        if (!response?.data?.success)
          throw new Error(response?.data?.errorMessage)

        this.dateFormats = response.data.value
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchDateFormats')
      }
    },
    async fetchOrDeductMinutesBalance(minutesToDeduct: number) {
      try {
        const response = await httpClient.get(
          '/web/api/MyAccount/GetCompleteCallingBalance'
        )
        if (!response?.data?.success)
          throw new Error(response?.data?.errorMessage)

        if (this.callingBalance && this.callingBalance == response.data.value) {
          this.callingBalance -= minutesToDeduct
        } else {
          this.callingBalance = response.data.value
        }
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchCallingBalance')
      }
    },
    getTimeZoneDictionary(): Map<string, ITimeZone> {
      const timeZoneMap = new Map<string, ITimeZone>()

      this.timeZones.forEach((t) => timeZoneMap.set(t.displayName || '', t))
      return timeZoneMap
    },
    getDateFormatDictionary(): Map<string, IDateFormat> {
      const dateFormatMap = new Map<string, IDateFormat>()

      this.dateFormats.forEach((d) =>
        dateFormatMap.set(
          `${d.formatName} ${d.shortDateFormat.toUpperCase()}`,
          d
        )
      )
      return dateFormatMap
    },
    async fetchSmsAccount() {
      try {
        const response = await httpClient.get(
          '/web/api/SmsNotification/GetAccount'
        )
        if (!response?.data?.success)
          throw new Error(response?.data?.errorMessage)

        this.smsAccount = response.data.value
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchSmsAccount')
      }
    },
    async verifyEmailChange(payload: IProcessEmailChangeRequest) {
      try {
        const url = '/web/api/MyAccount/ProcessEmailAddressChange'
        const result = await httpClientAnonymous.post(url, {
          Code: payload.code
        })

        if (!result.data.success) {
          const err = new Error(result.data.errorMessage)
          ErrorHelper.handleError(
            err,
            'verifyEmailChange',
            true,
            i18n.global.t('accountSettings.generalInfo.email.verify.errorMsg')
          )
          return
        } else {
          return result.data.value
        }
      } catch (e) {
        // 0 = generic server error
        ErrorHelper.handleError(e, 'verifyEmailChange', false)
        return
      }
    },
    async setPaywallPreviousPage(payload: IRouterLinkToObject) {
      this.paywallPreviousPage = payload
    },
    async confirmVerificationCode(code: string): Promise<boolean> {
      // commit('setEditPhoneNumberErrorMessage', null)
      try {
        const url = '/web/api/MyAccount/ConfirmVerificationCode'
        const result = await httpClient.post(url, { verifyCode: code })

        if (!result.data.success) {
          this.verifyPhoneSmsErrorCode = result.data.errorCode
        } else {
          this.fetchPhoneInfo()
        }
        return result.data.success
      } catch (e) {
        // 0 = generic server error
        ErrorHelper.handleError(e, 'confirmVerificationCode', false)
        return false
      }
    },
    async disableMfaSms(): Promise<boolean> {
      try {
        const url = '/web/api/MyAccount/DisableMfaSms'
        const result = await httpClient.post(url)
        const _reponseCamel: DisableMfaSmsResult = result.data
        if (!_reponseCamel.success) {
          const errorMessage = getLocalizedAccountSettingsError(
            'disableMfaSms',
            _reponseCamel
          )
          const err = new Error(errorMessage)
          ErrorHelper.handleError(err, 'disableMfaSms', true, errorMessage)
        } else {
          const login = useLoginStore()
          await login.setAuthTokenMethod(_reponseCamel.newToken || '')

          const commonStore = useCommonStore()
          await commonStore.fetchFullUserInfo()
        }
        return _reponseCamel.success || false
      } catch (e) {
        // 0 = generic server error
        ErrorHelper.handleError(e, 'disableMfaSms', false)
        return false
      }
    },
    async enableMfaSms(
      phoneNumber: string,
      isSignup?: boolean
    ): Promise<boolean> {
      try {
        const url = '/web/api/MyAccount/EnableMfaSms'
        const result = await httpClient.post(url, { phoneNumber: phoneNumber })

        this.setEnableMfaSmsErrorMessage('')
        const _reponseCamel: EnableMfaSmsResult = result.data
        if (!_reponseCamel.success) {
          const errorMessage = getLocalizedAccountSettingsError(
            'enableMfaSms',
            _reponseCamel
          )

          if (isSignup && _reponseCamel.errorCode) {
            this.setEnableMfaSmsErrorMessage(errorMessage)
            this.setEnableMfaSmsErrorCode(_reponseCamel.errorCode)
          }
          const err = new Error(errorMessage)
          ErrorHelper.handleError(err, 'enableMfaSms', true, errorMessage)
        }
        if (!isSignup) {
          const commonStore = useCommonStore()
          await commonStore.fetchFullUserInfo()
          await this.fetchPhoneInfo()
        }

        return _reponseCamel.success || false
      } catch (e) {
        // 0 = generic server error
        ErrorHelper.handleError(e, 'enableMfaSms', false)
        return false
      }
    },
    async confirmEnableMfaSms(code: string) {
      this.verifyPhoneSmsErrorMessage = ''

      try {
        const response = await httpClient.post(
          '/web/api/MyAccount/ConfirmEnableMfaSms',
          { code: code }
        )

        if (!response.data.success) {
          this.enableMfaSmsErrorCode = response.data.errorCode
        } else {
          const loginStore = useLoginStore()
          loginStore.setAuthTokenMethod(response.data.newToken)
          loginStore.setMfaCookieMethod(response.data.mfaCookie)
        }

        return response.data.success as boolean
      } catch (e) {
        ErrorHelper.handleError(e, 'confirmEnableMfaSms')
        return false
      }
    },
    async sendVerificationCode(phoneNumber: string): Promise<boolean> {
      const { closeActiveModal } = useModals()

      try {
        const url = '/web/api/MyAccount/SendVerificationCode'
        const result = await httpClient.post(
          url,
          { PhoneNumber: phoneNumber },
          { headers: { 'api-version': '2' } }
        )
        const _reponseCamel: ApiRequestResult = result.data
        if (!_reponseCamel.success) {
          closeActiveModal()

          const errorMessage = getLocalizedAccountSettingsError(
            'sendVerificationCode',
            _reponseCamel
          )
          const err = new Error(errorMessage)
          ErrorHelper.handleError(
            err,
            'sendVerificationCode',
            true,
            errorMessage
          )
        }
        return _reponseCamel.success || false
      } catch (e) {
        ErrorHelper.handleError(e, 'sendVerificationCode', false)
        return false
      }
    },
    async updateDateTimeSettings(
      timeZoneId: number,
      dateFormatId: number
    ): Promise<boolean> {
      try {
        const url = '/web/api/MyAccount/UpdateDateTimeSettings'
        const result = await httpClient.post(url, {
          timeZoneId: timeZoneId,
          dateFormatId: dateFormatId
        })
        const _reponseCamel: ApiRequestResult = result.data
        if (!_reponseCamel.success) {
          const err = new Error(_reponseCamel.errorMessage)
          ErrorHelper.handleError(
            err,
            'updateDateTimeSettings',
            true,
            _reponseCamel.errorMessage
          )
        } else {
          const common = useCommonStore()

          await this.fetchTimeZones()

          await common.fetchFullUserInfo()
        }
        return _reponseCamel.success || false
      } catch (e) {
        ErrorHelper.handleError(e, 'updateDateTimeSettings', false)
        return false
      }
    },
    setDateTimeSettings(timeZoneId: number, dateFormatId: number) {
      const timeZone = this.timeZones.find((t) => t.itemID == timeZoneId)
      const dateFormat = this.dateFormats.find((d) => d.itemID == dateFormatId)

      const commonStore = useCommonStore()

      commonStore.fullUserInfo.dateFormatId = dateFormatId
      commonStore.fullUserInfo.dateFormatName = dateFormat?.formatName
      commonStore.fullUserInfo.shortDateFormat =
        dateFormat?.shortDateFormat ?? 'M/d/yyyy'
      commonStore.fullUserInfo.timeZoneId = timeZoneId
      commonStore.fullUserInfo.timeZoneIdentifierString = timeZone?.identifier
      commonStore.fullUserInfo.timeZoneNameForMomentJs =
        timeZone?.displayName ?? ''
    },
    async hasUserAcceptedLatest(tosType: string): Promise<BooleanApiResult> {
      try {
        const response = await httpClient.get(
          '/web/api/TermsOfService/HasUserAcceptedLatest?tosType=' + tosType
        )
        if (!response?.data?.success)
          throw new Error(response?.data?.errorMessage)

        return response.data.value
      } catch (e) {
        ErrorHelper.handleError(e, 'hasUserAcceptedLatest')
        return new BooleanApiResult()
      }
    },
    async updateAllowCallsFromOtherUser(
      allowCallsFromOtherUser: boolean
    ): Promise<ApiRequestResult> {
      try {
        const url =
          '/web/api/MyAccount/UpdateAllowCallsFromOtherUser?allowCallsFromOtherUser=' +
          allowCallsFromOtherUser
        const result = await httpClient.post(url)
        const _responseCamel: ApiRequestResult = result.data
        if (!_responseCamel.success) {
          const err = new Error(_responseCamel.errorMessage)
          ErrorHelper.handleError(
            err,
            'updateAllowCallsFromOtherUser',
            true,
            _responseCamel.errorMessage
          )
        }
        return _responseCamel
      } catch (e) {
        ErrorHelper.handleError(e, 'sendVerificationCode', false)
        return new ApiRequestResult()
      }
    },
    async getLatestToS(tosType: string): Promise<ITosResult | null> {
      try {
        const response = await httpClient.get(
          '/web/api/TermsOfService/GetLatestToS?tosType=' + tosType
        )
        if (!response?.data?.success)
          throw new Error(response?.data?.errorMessage)

        const _reponseCamel: ITosResult = response.data.value
        return _reponseCamel
      } catch (e) {
        ErrorHelper.handleError(e, 'hasUserAcceptedLatest')
        return null
      }
    },
    async acceptTermsOfService(termsOfServiceId: number): Promise<boolean> {
      try {
        const response = await httpClient.post(
          '/web/api/TermsOfService/AcceptTermsOfService?termsOfServiceId=' +
            termsOfServiceId
        )
        if (!response?.data?.success)
          throw new Error(response?.data?.errorMessage)

        return response?.data?.success
      } catch (e) {
        ErrorHelper.handleError(e, 'hasUserAcceptedLatest')
        return false
      }
    },
    async updateAllowVideoCalls(
      val: boolean
    ): Promise<ApiRequestResult | null> {
      try {
        const url =
          '/web/api/MyAccount/UpdateAllowVideoCalls?allowVideoCalls=' + val
        const result = await httpClient.post(url)
        const _reponseCamel: ApiRequestResult = result.data.value

        if (!_reponseCamel.success) {
          const err = new Error(_reponseCamel.errorMessage)
          ErrorHelper.handleError(
            err,
            'updateAllowVideoCalls',
            true,
            _reponseCamel.errorMessage
          )
        }
        return _reponseCamel
      } catch (e) {
        ErrorHelper.handleError(e, 'hasUserAcceptedLatest')
        return null
      }
    },
    async updateSendNewMessageEmails(
      val: boolean
    ): Promise<ApiRequestResult | null> {
      try {
        const url =
          '/web/api/MyAccount/UpdateSendNewMessageEmails?sendNewMessageEmails=' +
          val
        const result = await httpClient.post(url)
        const _reponseCamel: ApiRequestResult = result.data
        if (!_reponseCamel.success) {
          const err = new Error(_reponseCamel.errorMessage)
          ErrorHelper.handleError(
            err,
            'updateAllowVideoCalls',
            true,
            _reponseCamel.errorMessage
          )
        }
        return _reponseCamel
      } catch (e) {
        ErrorHelper.handleError(e, 'hasUserAcceptedLatest')
        return null
      }
    },
    async refundSmsBalance(): Promise<any> {
      try {
        const url = '/web/api/SmsNotification/RefundBalance'
        const result = (await httpClient.post(url, undefined, {
          headers: { 'api-version': '2' }
        })) as any

        if (!result.success) {
          const err = new Error(result.errorMessage)

          ErrorHelper.handleError(
            err,
            'refundSmsBalance',
            true,
            result.errorMessage
          )
        }
        return result
      } catch (e) {
        ErrorHelper.handleError(e, 'refundSmsBalance')
        return null
      }
    },
    async changePassword(
      payload: IChangePasswordParam
    ): Promise<ChangePasswordResult | null> {
      try {
        const url = '/web/api/MyAccount/ChangePassword'
        const result = await httpClient.post(url, payload)
        const _reponseCamel: ChangePasswordResult = result.data.value

        if (!_reponseCamel.success) {
          const err = new Error(_reponseCamel.errorMessage)
          const _errorCode = _reponseCamel.errorCode || 0
          if (_errorCode == 0) {
            // 1 = Password incorrect
            // 2 = Inknown password format
            ErrorHelper.handleError(
              err,
              'updateAllowVideoCalls',
              true,
              _reponseCamel.errorMessage
            )
          }
        } else {
          const loginStore = useLoginStore()
          loginStore.setAuthTokenMethod(_reponseCamel.newAuthToken || '')
        }
        return _reponseCamel
      } catch (e) {
        ErrorHelper.handleError(e, 'changePassword')
        return null
      }
    },
    async removePhoneNumber() {
      try {
        const url = '/web/api/MyAccount/RemoveMobilePhoneInfo'
        const result = await httpClient.post(url, {})

        if (!result.data.success) {
          throw new Error(result.data.errorMessage)
        } else {
          const loginStore = useLoginStore()
          const commonStore = useCommonStore()

          loginStore.setAuthTokenMethod(result.data.value.newToken)
          commonStore.setMfaSmsEnabled(false)

          await this.fetchPhoneInfo()
          return result.data.success
        }
      } catch (e) {
        ErrorHelper.handleError(e, 'removePhoneNumber')
      }
    },
    async updateBrandConsistencyEnabled(enabled: boolean) {
      const url = 'api/MyAccount/UpdateBrandConsistencyEnabled'
      const payload = { enabled: enabled }
      const result = await httpClient.post(url, payload, {
        headers: { 'api-version': '2' }
      })
      return result.data.success
    },
    async updateName(firstName: string, lastName: string): Promise<boolean> {
      try {
        const url = '/web/api/MyAccount/SetRealName'
        const result = await httpClient.post(url, {
          firstName: firstName,
          lastName: lastName
        })
        const _reponseCamel: ApiRequestResult = result.data
        if (!_reponseCamel.success) {
          const err = new Error(_reponseCamel.errorMessage)
          ErrorHelper.handleError(
            err,
            'setRealName',
            true,
            _reponseCamel.errorMessage
          )
        }

        const commonStore = useCommonStore()

        commonStore.fullUserInfo.firstName = firstName
        commonStore.fullUserInfo.lastName = lastName
        commonStore.fullUserInfo.fullName = `${firstName} ${lastName}`
        return _reponseCamel.success || false
      } catch (e) {
        ErrorHelper.handleError(e, 'setRealName', false)
        return false
      }
    },
    async updateEmail(email: string): Promise<boolean> {
      try {
        const url = '/web/api/MyAccount/UpdateEmail'
        const result = await httpClient.post(
          url,
          { email: email },
          { headers: { 'api-version': '2' } }
        )
        const _reponseCamel: ApiRequestResult = result.data
        if (!_reponseCamel.success) {
          const errorMessage = getLocalizedAccountSettingsError(
            'updateEmail',
            _reponseCamel
          )
          const err = new Error(errorMessage)
          ErrorHelper.handleError(err, 'updateEmail', true, errorMessage)
        }

        return _reponseCamel.success || false
      } catch (e) {
        ErrorHelper.handleError(e, 'setRealName', false)
        return false
      }
    },
    async setVerifyPhoneSmsErrorMessage(errorMessage: string) {
      this.verifyPhoneSmsErrorMessage = errorMessage
    },
    async manageSubscription(currentRoute: string) {
      //stripe subscriptions: will always be in active array, never in suspended array, so manage plan button will show
      //BUT we disallow multiple stripe subscriptions, so we redirect back and show a modal
      //google/apple subscriptions: will be in suspended array if in retry state, and not in active array, so manage button will never show for those cases
      //the optionSelected function will show an error modal for suspended subs in these cases
      //for when the user has a suspended apple/google subscription
      const commonStore = useCommonStore()

      if (this.getHasSubscriptionIssue) {
        this.toggleSubscriptionIssueModal()
      } else if (this.isAppleSubscription) {
        this.toggleAppleSubscriptionModal()
      } else if (this.isAndroidSubscription) {
        this.toggleAndroidSubscriptionModal()
      } else if (this.isPromotional) {
        this.togglePromotionalSubscriptionModal()
      } else if (commonStore.fullUserInfo.subscriptionMigrationInProgress) {
        this.toggleSubscriptionMigrationModal()
      } else {
        const payloadPortal = new CreateCustomerPortalSessionRequest({
          returnUrl: `${import.meta.env.VITE_APP_BASE_URL}${currentRoute}`,
          priceId: '',
          planLevel: '',
          upgrade: false
        })

        const session = await this.createCustomerPortalSession(payloadPortal)

        if (session?.sessionUrl) {
          window.location.href = session.sessionUrl
        } else if (
          Number(session?.errorCode) ===
          CreateCustomerPortalSessionError.MigrationInProgress
        ) {
          this.toggleSubscriptionMigrationModal()
        }
      }
    },

    toggleSubscriptionIssueModal() {
      const { generateModal, createSlot, HTMLtoComponent } = useModals()

      let text: string[] = []

      if (this.getActiveSubscription) {
        switch (this.subscriptionInfo.duplicateSubscriptionError) {
          case constants.duplicateSubscriptionError.AppleApple:
            text = ['Apple App Store']
            break
          case constants.duplicateSubscriptionError.BraintreeApple:
            text = ['TalkingParents.com', 'Apple App Store']
            break
          case constants.duplicateSubscriptionError.BraintreeBraintree:
            text = ['TalkingParents.com']
            break
        }
      }

      const subscriptionsText = `
      <div class="stack size-1">
        <span>${i18n.global.t('accountSettings.planDetails.multiSubModal.subList')}</span>
        <ul>
            ${Object.keys(text)
              .map(
                (key: any) =>
                  `<li><span class="brand underline">${text[key]}</span></li>`
              )
              .join('')}
        </ul>
        <span>${i18n.global.t('accountSettings.planDetails.multiSubModal.cancelText')}</span>
        <span class="medium-gray font-size-0">${i18n.global.t('accountSettings.planDetails.multiSubModal.tpSub')}</span>
      </div>`

      generateModal({
        slot: createSlot('content', HTMLtoComponent(subscriptionsText)),
        default: {
          headerText: i18n.global.t(
            'accountSettings.planDetails.multiSubModal.title'
          )
        },
        config: { showFooter: false }
      })
    },
    toggleAppleSubscriptionModal() {
      const { generateModal } = useModals()
      generateModal({
        default: {
          headerText: i18n.global.t(
            'accountSettings.planDetails.appleSubModal.title'
          ),
          contentText: i18n.global.t(
            'accountSettings.planDetails.appleSubModal.content'
          ),
          footerButtonLabel: i18n.global.t(
            'accountSettings.planDetails.appleSubModal.cta'
          )
        },
        config: { closeOnConfirm: true },
        callback: { confirmFn: this.handleMobileModalButtonClick }
      })
    },
    toggleAndroidSubscriptionModal() {
      const { generateModal } = useModals()
      generateModal({
        default: {
          headerText: i18n.global.t(
            'accountSettings.planDetails.androidSubModal.title'
          ),
          contentText: i18n.global.t(
            'accountSettings.planDetails.androidSubModal.content'
          ),
          footerButtonLabel: i18n.global.t(
            'accountSettings.planDetails.androidSubModal.cta'
          )
        },
        config: { closeOnConfirm: true },
        callback: { confirmFn: this.handleMobileModalButtonClick }
      })
    },
    togglePromotionalSubscriptionModal() {
      const { generateModal } = useModals()
      generateModal({
        default: {
          headerText: i18n.global.t(
            'accountSettings.planDetails.complimentaryPlanModal.title'
          ),
          contentText: i18n.global.t(
            'accountSettings.planDetails.complimentaryPlanModal.content'
          ),
          footerButtonLabel: i18n.global.t(
            'accountSettings.planDetails.complimentaryPlanModal.cta'
          )
        },
        config: { closeOnConfirm: true },
        callback: {
          confirmFn: () =>
            (window.location.href = 'mailto:support@talkingparents.com')
        }
      })
    },
    toggleSubscriptionMigrationModal() {
      const { generateModal } = useModals()
      generateModal({
        default: {
          headerText: i18n.global.t(
            'accountSettings.planDetails.subscriptionMigrationModal.title'
          ),
          contentText: i18n.global.t(
            'accountSettings.planDetails.subscriptionMigrationModal.content'
          ),
          footerButtonLabel: i18n.global.t(
            'accountSettings.planDetails.subscriptionMigrationModal.cta'
          )
        },
        config: { closeOnConfirm: true },
        callback: {
          confirmFn: () =>
            (window.location.href = 'mailto:support@talkingparents.com')
        }
      })
    },
    handleMobileModalButtonClick() {
      const indexOfAndroid = navigator.userAgent
        .toLowerCase()
        .indexOf('android')
      const indexOfIPhone = navigator.userAgent.toLowerCase().indexOf('iphone')

      if (indexOfAndroid > -1 && this.isAndroidSubscription) {
        window.location.href =
          'intent://play.google.com/store/account/subscriptions#Intent;scheme=https;package=com.android.vending;end'
      } else if (this.isAndroidSubscription) {
        window.location.href =
          'https://play.google.com/store/account/subscriptions'
      } else if (indexOfIPhone > -1 && this.isAppleSubscription) {
        window.location.href =
          'itms-apps://apps.apple.com/account/subscriptions'
      }
    },
    setConsentUrl(consentUrl: string) {
      this.consentUrl = consentUrl
    },
    setEnableMfaSmsErrorMessage(enableMfaSmsErrorMessage: string) {
      this.enableMfaSmsErrorMessage = enableMfaSmsErrorMessage
    },
    setEnableMfaSmsErrorCode(enableMfaSmsErrorCode: number) {
      this.enableMfaSmsErrorCode = enableMfaSmsErrorCode
    },
    setVerifyPhoneSmsErrorCode(verifyPhoneSmsErrorCode: number) {
      this.verifyPhoneSmsErrorCode = verifyPhoneSmsErrorCode
    },
    setShowAddMinutesSuccessToast(showToast: boolean) {
      this.showAddMinutesSuccessToast = showToast
    },
    getSubscriptionPrice(level: string) {
      const common = useCommonStore()
      return common.appSettings?.stripeSubscriptions
        ? common.appSettings?.stripeSubscriptions.find(
            (x) => x.entitlementIdentifier == level && x.legacy == false
          )?.price
        : undefined
    },
    async getStripePriceId(level: string) {
      const common = useCommonStore()
      return common.appSettings?.stripeSubscriptions
        ? common.appSettings?.stripeSubscriptions.find(
            (x) => x.entitlementIdentifier == level && x.legacy == false
          )?.priceId
        : ''
    },
    async getStripePriceIdForProduct(key: string) {
      const common = useCommonStore()
      return common.appSettings?.stripeProducts
        ? common.appSettings?.stripeProducts?.products
            ?.find((r) => r.key == key)
            ?.priceId?.trim()
        : ''
    },
    getStripeEntitlementIdentifierForProductId(id: string) {
      const commonStore = useCommonStore()

      return commonStore.appSettings?.stripeSubscriptions?.find(
        (s) => s.productId == id
      )?.entitlementIdentifier
    },
    async updateAIEventSuggestionsEnabled(isEnabled: boolean) {
      try {
        const url = '/web/api/MyAccount/UpdateAICalendarEventSuggestionsEnabled'
        const result = await httpClient.put(url, { enabled: isEnabled })

        return result.data.success
      } catch (e) {
        ErrorHelper.handleError(
          e,
          'UpdateAICalendarEventSuggestionsEnabled',
          false
        )
        return false
      }
    }
  }
})
